// EVERHOPE-520 cache-bust 2026-06-10
// EVERHOPE-497 Phase B — 11 DOM-bound GA4 events pushed to dataLayer.
//
// Loaded after marketing-params-custom.js so window.everhopeMarketingParams.read()
// is available for UTM enrichment. GTM container (GTM-KJK7CV4B) is loaded
// earlier in <head> via theme.liquid and forwards every event from
// window.dataLayer to GA4 property G-TFTR01N60J.
//
// EVERHOPE-524: also fires Meta Pixel (fbq) AddToCart + InitiateCheckout —
// carried over from the retired analytics-events.js so Facebook/Instagram ad
// conversion tracking survives the live-theme cutover. GA4 add_to_cart /
// begin_checkout are NOT duplicated here; the Admin Custom Pixel owns those.
//
// Heuristic-first design: no theme template edits required. Section names
// are derived from Shopify's `shopify-section--<type>` class wrapper, product
// info from PDP DOM, list context from enclosing section + sibling index.
// If accuracy is off for any event, swap heuristic for explicit data-ga-*
// attributes on the relevant section liquids in a follow-up.

(() => {
  'use strict';

  // ---- page-type detection (location.pathname is the most reliable signal
  // across Shopify themes; body classes vary) ---------------------------
  const path        = () => window.location.pathname;
  const isHomepage  = () => path() === '/' || path() === '';
  const isPDP       = () => path().startsWith('/products/');
  const isSearch    = () => path() === '/search';

  // ---- UTM enrichment from EVERHOPE-499 storage -----------------------
  function getUTMs() {
    try {
      const stored = (window.everhopeMarketingParams && window.everhopeMarketingParams.read()) || {};
      const out = {};
      ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'].forEach(k => {
        if (stored['_' + k]) out[k] = stored['_' + k];
      });
      return out;
    } catch (_) { return {}; }
  }

  function push(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, getUTMs(), payload));
  }

  function throttle(fn, ms) {
    let last = 0, timer = null;
    return function () {
      const ctx = this, args = arguments;
      const now = Date.now();
      const wait = ms - (now - last);
      if (wait <= 0) {
        if (timer) { clearTimeout(timer); timer = null; }
        last = now;
        fn.apply(ctx, args);
      } else if (!timer) {
        timer = setTimeout(() => {
          last = Date.now();
          timer = null;
          fn.apply(ctx, args);
        }, wait);
      }
    };
  }

  // ---- PDP item context — multi-strategy variant lookup because Prestige
  // disables the buy-buttons hidden input when a variant picker exists,
  // and document.querySelector('input[name="id"]') can match the wrong
  // form (related products, quick-buys) higher in the DOM. Order matters:
  //   1. ?variant=<id> from URL — Shopify's canonical after selection
  //   2. <product-form> input[name="id"] — scoped to the main product form
  //   3. JSON-LD offer.sku — last resort, parses structured data
  function getPDPItemContext() {
    if (!isPDP()) return null;
    let variantId = '';

    // 1. URL query — Shopify rewrites to /products/<handle>?variant=<id>
    //    on variant selection for multi-variant products.
    try {
      const v = new URLSearchParams(location.search).get('variant');
      if (v) variantId = v;
    } catch (_) {}

    // 2. Product-form-scoped input
    if (!variantId) {
      const candidates = document.querySelectorAll('product-form input[name="id"], form[action*="/cart/add"] input[name="id"], input[name="id"]');
      for (let i = 0; i < candidates.length; i++) {
        const v = candidates[i].value;
        if (v) { variantId = v; break; }
      }
    }

    // 3. JSON-LD offer parsing
    if (!variantId) {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      for (let i = 0; i < scripts.length && !variantId; i++) {
        try {
          const data = JSON.parse(scripts[i].textContent);
          const items = Array.isArray(data) ? data : [data];
          for (const it of items) {
            if (!it || (it['@type'] !== 'Product' && it['@type'] !== 'ProductGroup')) continue;
            const offers = Array.isArray(it.offers) ? it.offers : (it.offers ? [it.offers] : []);
            for (const o of offers) {
              if (o && (o.sku || o['@id'])) { variantId = String(o.sku || o['@id']); break; }
            }
            if (variantId) break;
          }
        } catch (_) {}
      }
    }

    const h1 = document.querySelector('h1');
    return {
      item_id:   variantId,
      item_name: (h1 && h1.innerText.trim()) || document.title.split(/[-|]/)[0].trim()
    };
  }

  // ---- Section detection — every Shopify section wraps in a div with
  // class "shopify-section shopify-section--<type>" + id "shopify-section-<id>"
  // Template-bound sections (defined in templates/product.json etc.) get an
  // extra template--<id>__ prefix in their id; strip it for cleaner names.
  function findEnclosingSection(el) {
    const wrapper = el.closest && el.closest('.shopify-section');
    if (!wrapper) return null;
    // Allow underscores (e.g. shopify-section--what_makes_different) — earlier
    // regex was [a-z0-9-]+ which silently fell back to the ID for any
    // section type with underscores in its name.
    const m = wrapper.className.match(/shopify-section--([a-z0-9_-]+)/);
    let name = m ? m[1] : wrapper.id.replace('shopify-section-', '');
    // Strip template--<digits>__ template-binding prefix
    name = name.replace(/^template--\d+__/, '').slice(0, 60);
    return { id: wrapper.id, name: name, el: wrapper };
  }

  // ---- Section-view noise filter — these are overlays/structural sections
  // that fire IntersectionObserver but don't represent meaningful content
  // engagement. Suppress for both homepage_section_view and pdp_section_view.
  const SECTION_NAME_DENY = new Set([
    'announcement-bar', 'header', 'header-sidebar',
    'footer', 'footer-custom', 'footer-hero-custom', 'footer-group',
    'cart-drawer', 'popup', 'overlay-group',
    'apps'
  ]);
  function isNoiseSection(name) {
    return SECTION_NAME_DENY.has(name);
  }

  // ============================================================
  // EVENT 1: select_item — click on product link inside a list section
  // ============================================================
  document.addEventListener('click', (e) => {
    const link = e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!link) return;

    const section = findEnclosingSection(link);
    if (!section) return;
    // Skip the PDP's own main section so back-to-self links don't fire
    if (/main-product|product-hero/.test(section.name)) return;

    // Index among siblings of the wrapping card
    const card = link.closest('[class*="card"], [class*="product-item"], li, article');
    let index = 0;
    if (card && card.parentElement) {
      const siblings = Array.from(card.parentElement.children);
      const i = siblings.indexOf(card);
      if (i >= 0) index = i;
    }

    const handleMatch = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    const handle = handleMatch ? handleMatch[1] : '';

    const nameEl = (card && card.querySelector('h3, h2, .card__title, [class*="product-title"], [class*="card-title"]')) || null;
    const itemName = (nameEl && nameEl.innerText.trim()) || handle.replace(/-/g, ' ');

    push('select_item', {
      ecommerce: {
        item_list_id:   section.id,
        item_list_name: section.name,
        items: [{ item_id: handle, item_name: itemName.slice(0, 200), index: index }]
      }
    });
  }, { capture: true });

  // ============================================================
  // EVENT 2: search — form submit + post-results count on /search
  // ============================================================
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/search"]')) return;
    const input = form.querySelector('input[name="q"]');
    const term = (input && input.value && input.value.trim()) || '';
    if (!term) return;
    push('search', { search_term: term, results_count: null });
  }, { capture: true });

  // On /search results page, fire a second event once results have rendered
  // with the actual results_count populated.
  if (isSearch()) {
    const term = new URLSearchParams(location.search).get('q');
    if (term) {
      setTimeout(() => {
        let count = null;
        const countEl = document.querySelector('[data-search-results-count], [class*="search-result-count"], [class*="results-count"]');
        if (countEl) {
          const m = countEl.innerText.match(/\d+/);
          count = m ? parseInt(m[0], 10) : null;
        }
        if (count === null) {
          count = document.querySelectorAll('a[href*="/products/"]').length;
        }
        push('search', { search_term: term, results_count: count });
      }, 800);
    }
  }

  // ============================================================
  // EVENTS 4 & 5: pdp_scroll_50 / pdp_scroll_100
  // ============================================================
  if (isPDP()) {
    let fired50 = false, fired100 = false;
    const itemCtx = getPDPItemContext() || {};
    const onScroll = throttle(() => {
      const scrolled  = window.scrollY + window.innerHeight;
      const maxScroll = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      if (maxScroll <= 0) return;
      const pct = scrolled / maxScroll;
      if (!fired50 && pct >= 0.5)   { fired50  = true; push('pdp_scroll_50',  Object.assign({}, itemCtx)); }
      if (!fired100 && pct >= 0.95) { fired100 = true; push('pdp_scroll_100', Object.assign({}, itemCtx)); }
      if (fired50 && fired100) window.removeEventListener('scroll', onScroll);
    }, 250);
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(onScroll, 600); // initial check for short PDPs
  }

  // ============================================================
  // EVERHOPE-520 EVENTS 12 & 13: homepage_scroll_50 / homepage_scroll_100
  // ============================================================
  // Mirror of the PDP scroll block, gated on isHomepage(). Part 1 only
  // tracked scroll depth on PDPs; the homepage had no equivalent.
  if (isHomepage()) {
    let hp50 = false, hp100 = false;
    const onHpScroll = throttle(() => {
      const scrolled  = window.scrollY + window.innerHeight;
      const maxScroll = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
      if (maxScroll <= 0) return;
      const pct = scrolled / maxScroll;
      if (!hp50 && pct >= 0.5)   { hp50  = true; push('homepage_scroll_50',  {}); }
      if (!hp100 && pct >= 0.95) { hp100 = true; push('homepage_scroll_100', {}); }
      if (hp50 && hp100) window.removeEventListener('scroll', onHpScroll);
    }, 250);
    window.addEventListener('scroll', onHpScroll, { passive: true });
    setTimeout(onHpScroll, 600); // initial check for short homepages
  }

  // ============================================================
  // EVENT 6: login — form submit on /account/login
  // ============================================================
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[action*="/account/login"]')) return;
    push('login', { login_method: 'email' });
  }, { capture: true });

  // ============================================================
  // EVENT 7: quantity_change — diff cart snapshots between cart events
  // ============================================================
  // No DOM dependence: snapshot cart contents from /cart.js, compare on
  // each cart:refresh / cart:change. Fires only on quantity diffs of
  // already-present items (not on first-add — that's add_to_cart's job).
  let lastCart = {};
  let cartReady = false;

  async function snapshotCart() {
    try {
      /* WH-172: bounded/retrying fetch, plain-fetch fallback */
      const r = await (window.EH && window.EH.fetchRetry
        ? window.EH.fetchRetry('/cart.js', { credentials: 'same-origin' })
        : fetch('/cart.js', { credentials: 'same-origin' }));
      if (!r.ok) return null;
      const cart = await r.json();
      const map = {};
      (cart.items || []).forEach(it => {
        map[String(it.variant_id)] = { qty: it.quantity, title: it.product_title || it.title };
      });
      return map;
    } catch (_) { return null; }
  }

  async function onCartEvent() {
    const cur = await snapshotCart();
    if (!cur) return;
    if (!cartReady) { lastCart = cur; cartReady = true; return; }

    Object.keys(cur).forEach(vid => {
      const oldQty = (lastCart[vid] && lastCart[vid].qty) || 0;
      const newQty = cur[vid].qty;
      if (oldQty > 0 && newQty > 0 && oldQty !== newQty) {
        push('quantity_change', {
          item_id:         vid,
          item_name:       cur[vid].title,
          quantity_before: oldQty,
          quantity_after:  newQty,
          quantity_action: newQty > oldQty ? 'increment' : 'decrement'
        });
      }
    });
    lastCart = cur;
  }

  const debouncedCartEvent = (() => {
    let t = null;
    return () => { if (t) clearTimeout(t); t = setTimeout(onCartEvent, 250); };
  })();
  document.addEventListener('cart:refresh', debouncedCartEvent);
  document.addEventListener('cart:change',  debouncedCartEvent);
  // Initial snapshot so the first cart event doesn't see qty 0 -> qty N as a "change"
  setTimeout(() => snapshotCart().then(m => { if (m && !cartReady) { lastCart = m; cartReady = true; } }), 500);

  // ============================================================
  // META PIXEL (Facebook) — AddToCart + InitiateCheckout
  // ============================================================
  // GA4's add_to_cart / begin_checkout are already covered by the Admin
  // Custom Pixel (product_added_to_cart / checkout_started), so we do NOT
  // re-fire them here. These are the *Meta* (fbq) equivalents only, carried
  // over from the retired analytics-events.js so Facebook/Instagram ad
  // conversion tracking is preserved. fbq is injected by the Facebook &
  // Instagram sales channel; every call is guarded to no-op if it's absent.
  function metaTrack(event, payload) {
    if (!window.fbq) return;
    try { window.fbq('track', event, payload); } catch (_) {}
  }

  // AddToCart — invoked by the custom section JS (care-packs, shop-by-symptoms,
  // support-hero-benefits, product-hero-custom, collection-custom) through the
  // window.fireAddToCartEvent hook. Those sections call it behind a
  // `if (window.fireAddToCartEvent)` guard, so defining it here re-enables them.
  window.fireAddToCartEvent = function (variantId, productTitle, priceInRupees, qty) {
    qty = qty || 1;
    var value = (priceInRupees || 0) * qty;
    metaTrack('AddToCart', {
      content_ids:  [String(variantId)],
      content_name: productTitle || undefined,
      content_type: 'product',
      value:        value,
      currency:     'INR'
    });
  };

  // InitiateCheckout — fires on add-to-cart (cart:change / baseEvent
  // 'variant:add'), matching the behaviour of the retired analytics-events.js.
  // Reads the cart total from the event detail. Separate listener from the
  // quantity_change handler above; both may run on the same cart:change.
  document.addEventListener('cart:change', function (e) {
    if (!e.detail || e.detail.baseEvent !== 'variant:add') return;
    var cart = e.detail.cart;
    if (!cart) return;
    metaTrack('InitiateCheckout', {
      value:     (cart.total_price || 0) / 100,
      currency:  'INR',
      num_items: cart.item_count || 0
    });
  });

  // ============================================================
  // EVENT 8: pdp_image_interaction — click on PDP gallery
  // ============================================================
  // Prestige's gallery DOM:
  //   <product-gallery>
  //     <scroll-carousel>
  //       <div class="product-gallery__media" data-media-id="..."> ... <img/> ...
  //     <product-gallery-navigation>     (thumbnail rail)
  //       <button> ... <img/> ...
  // We catch clicks on EITHER: a thumbnail button in product-gallery-navigation,
  // OR a .product-gallery__media tile in the main carousel. Index is the
  // position among siblings of the same type (media-tile or button).
  document.addEventListener('click', (e) => {
    if (!isPDP()) return;
    const target = e.target;
    if (!target || !target.closest) return;

    const gallery = target.closest('product-gallery, .product-gallery');
    if (!gallery) return;

    // Thumbnail rail button OR main carousel media tile
    const thumbButton = target.closest('product-gallery-navigation button, .product-gallery__thumbnail-list button');
    const mediaTile   = target.closest('.product-gallery__media, [data-media-id]');
    const item = thumbButton || mediaTile;
    if (!item) return;

    // Index among same-type siblings within the immediate parent
    let index = 0;
    if (item.parentElement) {
      const siblings = Array.from(item.parentElement.children).filter(c => {
        if (thumbButton) return c.tagName === 'BUTTON' || c.querySelector('button');
        return c.matches('.product-gallery__media, [data-media-id]');
      });
      const i = siblings.indexOf(item);
      if (i >= 0) index = i;
    }

    push('pdp_image_interaction', Object.assign({ image_index: index }, getPDPItemContext() || {}));
  }, { capture: true });

  // ============================================================
  // EVENTS 9 & 10: pdp_section_view, homepage_section_view
  // ============================================================
  const firedSections = new Set();

  function setupSectionViewObserver() {
    if (typeof IntersectionObserver === 'undefined') return;
    const sections = document.querySelectorAll('.shopify-section');
    if (!sections.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const w = entry.target;
        if (firedSections.has(w.id)) return;
        firedSections.add(w.id);

        // Reuse findEnclosingSection for consistent name extraction
        // (regex + template-prefix strip).
        const sectionInfo = findEnclosingSection(w);
        const sectionName = sectionInfo ? sectionInfo.name : w.id.replace('shopify-section-', '').slice(0, 60);

        if (isNoiseSection(sectionName)) return;

        if (isPDP()) {
          push('pdp_section_view', Object.assign({ section_name: sectionName }, getPDPItemContext() || {}));
        } else if (isHomepage()) {
          push('homepage_section_view', { section_name: sectionName });
        }
      });
    }, { threshold: 0.5 });

    sections.forEach(s => observer.observe(s));
  }

  // ============================================================
  // EVENT 11: homepage_cta_click — clicks on CTA-style elements
  // ============================================================
  // Heuristic: Prestige buttons match `a.button` / `button.button` /
  // `*[class*="button--"]`. Also honors explicit `[data-ga-cta]` for
  // future precision. Skips clicks that bubble through product cards
  // (those are select_item) and form-submit buttons (those are search/login).
  document.addEventListener('click', (e) => {
    if (!isHomepage()) return;
    const target = e.target;
    if (!target || !target.closest) return;

    // Match: explicit data-ga-cta override, Prestige's `.button` / `.button--*`
    // pattern, BEM `*__cta` (e.g. hero-slide__cta), BEM `*__buy-btn` (e.g.
    // cp-panel__buy-btn from care-packs), and generic `cta-button`.
    const cta = target.closest(
      '[data-ga-cta], a.button, button.button, ' +
      'a[class*="button--"], button[class*="button--"], a.btn, button.btn, ' +
      '[class*="__cta"], [class*="__buy-btn"], [class*="cta-button"]'
    );
    if (!cta) return;
    // Skip form-submit buttons (those have their own events)
    if (cta.type === 'submit' || cta.closest('form[action*="/search"], form[action*="/account/login"]')) return;
    // EVERHOPE-520: care-pack buy buttons match the generic `__buy-btn` pattern
    // but have their own carepack_buy_now event — don't double-count.
    if (cta.matches('.cp-panel__buy-btn')) return;

    const section = findEnclosingSection(cta);
    if (!section) return;

    const label = (cta.getAttribute('data-ga-cta') ||
                   (cta.innerText && cta.innerText.trim()) ||
                   cta.getAttribute('aria-label') ||
                   '').slice(0, 100);
    if (!label) return;

    push('homepage_cta_click', {
      cta_label:    label,
      section_name: section.name
    });
  }, { capture: true });

  // ============================================================
  // EVERHOPE-520 EVENT 14: category_click — tap on a "Shop By Symptoms" tab
  // ============================================================
  // The 6 category tabs are <button class="sbs-tab" data-collection="...">
  // toggles (not /products/ links), so select_item never sees them. Every
  // tap counts — re-taps included — per "which category gets most taps".
  document.addEventListener('click', (e) => {
    const tab = e.target.closest && e.target.closest('.sbs-tab');
    if (!tab) return;
    const handle = tab.getAttribute('data-collection') || '';
    const name   = (tab.innerText && tab.innerText.trim()) || handle;
    push('category_click', {
      category_handle: handle,
      category_name:   name.slice(0, 100)
    });
  }, { capture: true });

  // ============================================================
  // EVERHOPE-520 EVENT 15: faq_toggle — FAQ question expanded
  // ============================================================
  // Fire on EXPAND only. In the capture phase we run before the theme's own
  // handler flips the state, so aria-expanded still holds the pre-click value:
  // 'false' => the user is opening it. Skip 'true' (collapsing) and the
  // first-item open-by-default render (not a user interaction).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('[data-faq-btn]');
    if (!btn) return;
    if (btn.getAttribute('aria-expanded') === 'true') return; // collapsing
    const qEl = btn.querySelector('.faq-question__text');
    const question = (qEl && qEl.innerText.trim()) ||
                     (btn.innerText && btn.innerText.trim()) || '';
    const section = findEnclosingSection(btn);
    push('faq_toggle', {
      faq_question: question.slice(0, 200),
      section_name: section ? section.name : ''
    });
  }, { capture: true });

  // ============================================================
  // EVERHOPE-520 EVENT 16: book_consult_click — Expert Consult banner CTA
  // ============================================================
  // a.ecb-btn lives in expert-consult-banner.liquid and can appear on any
  // page, so this is page-agnostic (not gated on isHomepage). ecb-btn matches
  // none of the homepage_cta_click selectors, so no dedup needed.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('a.ecb-btn');
    if (!btn) return;
    push('book_consult_click', {
      cta_label: ((btn.innerText && btn.innerText.trim()) || '').slice(0, 100),
      link_url:  btn.getAttribute('href') || ''
    });
  }, { capture: true });

  // ============================================================
  // EVERHOPE-520 EVENT 17: carepack_buy_now — care-pack "Buy All" button
  // ============================================================
  // .cp-panel__buy-btn inside an enclosing .cp-panel that carries either
  // data-collection (collection-sourced pack) or data-bundle-product (Shopify
  // bundle). Panels render async via care-packs.js, so delegate off document.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.cp-panel__buy-btn');
    if (!btn) return;
    const panel = btn.closest('.cp-panel');
    const handle = panel
      ? (panel.getAttribute('data-collection') || panel.getAttribute('data-bundle-product') || '')
      : '';
    push('carepack_buy_now', {
      pack_handle: handle,
      cta_label:   ((btn.innerText && btn.innerText.trim()) || '').slice(0, 100)
    });
  }, { capture: true });

  // ============================================================
  // EVERHOPE-520 EVENTS 18-20: newsletter_signup / contact_form_submit /
  //                            ask_expert_submit (form submits)
  // ============================================================
  // Newsletter ({% form 'customer' %}) and contact ({% form 'contact' %}) BOTH
  // post to /contact — discriminate by form_type + the contact[tags] hidden
  // field, never by action URL. We fire on submit (intent); confirmed success
  // would require detecting the post-redirect success banner (see plan).
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form || !form.matches) return;

    // Ask-Expert inline form (care-packs). Send only the length — the free-text
    // is user PII and must not reach GA4.
    if (form.matches('form[data-ask-form]')) {
      const input = form.querySelector('.cp-ask__input, input[name="ask"]');
      const len = (input && input.value && input.value.trim().length) || 0;
      push('ask_expert_submit', { question_length: len });
      return;
    }

    const formType = (form.querySelector('input[name="form_type"]') || {}).value || '';
    const tagsEl   = form.querySelector('input[name="contact[tags]"]');
    const isNewsletter = (tagsEl && /newsletter/.test(tagsEl.value || '')) || formType === 'customer';

    if (isNewsletter) {
      let loc = 'section';
      if (form.closest('newsletter-popup')) loc = 'popup';
      else if (form.closest('.footer, footer, [class*="footer"]')) loc = 'footer';
      push('newsletter_signup', { signup_location: loc });
      return;
    }

    if (formType === 'contact' || form.querySelector('textarea[name="contact[body]"]')) {
      const section = findEnclosingSection(form);
      push('contact_form_submit', { form_location: section ? section.name : '' });
    }
  }, { capture: true });

  // ============================================================
  // PUBLIC SURFACE — EVERHOPE-500 (pincode) calls this; other code can too
  // ============================================================
  window.everhopeAnalytics = {
    trackSelectItem(handle, listId, listName, index, itemName) {
      push('select_item', {
        ecommerce: {
          item_list_id:   listId,
          item_list_name: listName,
          items: [{ item_id: handle, item_name: itemName, index: index || 0 }]
        }
      });
    },
    trackSearch(term, count) {
      push('search', { search_term: term, results_count: typeof count === 'number' ? count : null });
    },
    trackPincodeCheck(pincode, serviceable) {
      push('pincode_check', { pincode: pincode, serviceable: serviceable ? 'yes' : 'no' });
    },
    trackLogin(method) {
      push('login', { login_method: method || 'email' });
    },
    trackPDPImageInteraction(index) {
      push('pdp_image_interaction', Object.assign({ image_index: index }, getPDPItemContext() || {}));
    }
  };

  // ============================================================
  // INIT
  // ============================================================
  function init() {
    setupSectionViewObserver();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
